import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQuery';
import { 
  IUserManagementListResponse,
  IUserManagementAccountForm,
  IUserManagementRoleForm,
  IUserRoleDelete
} from '@/types/admin/userManagement';

// NOTE: User management API doesn't support server-side pagination/filtering like resident list
// It returns all users at once, and we do client-side filtering/sorting/pagination

// API Response interfaces
interface IRoleResponse {
  success: boolean;
  response?: any;
  message?: string;
}

export const userManagementApi = createApi({
  reducerPath: 'userManagementApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['UserRoles'],
  endpoints: (builder) => ({
    // Get user roles list (matching admin-web endpoint - fetches ALL users)
    getUserRoles: builder.query<IUserManagementListResponse, undefined>({
      query: () => ({
        url: '/api/v1/roles/list',
        method: 'GET',
      }),
      providesTags: ['UserRoles'],
    }),

    // Create user (matching admin-web endpoint)
    createUser: builder.mutation<IRoleResponse, IUserManagementAccountForm>({
      query: (body) => ({
        url: '/api/v2/users/staff-create-account',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['UserRoles'],
    }),

    // Edit user (matching admin-web endpoint)
    editUser: builder.mutation<IRoleResponse, IUserManagementAccountForm>({
      query: (body) => ({
        url: '/api/v2/users/staff-update-account',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['UserRoles'],
    }),

    // Delete user (matching admin-web endpoint)
    deleteUser: builder.mutation<IRoleResponse, { accountId: number }>({
      query: ({ accountId }) => ({
        url: `/api/v2/users/staff-delete-account/${accountId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UserRoles'],
    }),

    // Assign roles (matching admin-web endpoint)
    assignRoles: builder.mutation<IRoleResponse, IUserManagementRoleForm>({
      query: (body) => ({
        url: '/api/v1/roles/assign-roles',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['UserRoles'],
    }),

    // Revoke role (matching admin-web endpoint)
    revokeRole: builder.mutation<IRoleResponse, IUserRoleDelete>({
      query: (body) => ({
        url: '/api/v1/roles/revoke-role',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['UserRoles'],
    }),
  }),
});

export const {
  useLazyGetUserRolesQuery,
  useCreateUserMutation,
  useEditUserMutation,
  useDeleteUserMutation,
  useAssignRolesMutation,
  useRevokeRoleMutation,
} = userManagementApi;
